import PageHeader from "@/components/page-header";
import { CheckPaymentSuccessDialog } from "@/components/registrant/check-payment-success-dialog";
import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { LicenseTypeQuestionsForm } from "@/components/registrant/license-form/license-type-questions";
import { PeriodsAndFeesForm } from "@/components/registrant/license-form/periods-and-fees";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import {
  getFeesForRenewalQueryKey,
  getLicenseDetailsOptions,
  updateLicenseAdditionalQuestionsMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useLicenseFormData } from "@/hooks/use-license-form-data";
import { useLicenseFormMutations } from "@/hooks/use-license-form-mutations";
import { useStepManager } from "@/hooks/use-step-manager";
import { extractFeeAnswerOverrides } from "@/lib/extract-fee-answer-overrides";
import { invalidateRegistrantDashboardLists } from "@/lib/registrant-dashboard-query-invalidation";
import { getRegistrationTaggedQuestion } from "@/lib/service-transfer-wizard";
import { partyIdFromUnregisteredAnswer } from "@/lib/unregistered-registration-answers";
import {
  getQueryErrorHeading,
  getQueryErrorMessage,
  isRejected,
} from "@/lib/utils";
import type { LicenseAnswers, PeriodsAndFeesData } from "@/types/license";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { validate as uuidValidate } from "uuid";

export const Route = createFileRoute("/registrant/licenses/$licenseId/renew")({
  component: RenewLicenseScreen,
});

enum RenewLicenseStep {
  LicenseTypeQuestions = 1,
  PeriodsAndFees = 2,
}

export function RenewLicenseScreen() {
  const { licenseId } = Route.useParams();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const isValidLicenseId = uuidValidate(licenseId);
  const [licenseAnswers, setLicenseAnswers] = useState<LicenseAnswers | null>(
    null,
  );
  const [step, setStep] = useState(RenewLicenseStep.LicenseTypeQuestions);
  const [checkPaymentSuccess, setCheckPaymentSuccess] = useState<{
    displayId: string;
    checkMailingAddress?: string | null;
    variant: "check" | "invoice";
  } | null>(null);

  const { stepNumber, nextStep, previousStep } = useStepManager({
    maxSteps: 2,
  });

  // Get license details
  const {
    data: licenseData,
    isLoading: isQueryLoading,
    error: queryError,
  } = useQuery({
    ...getLicenseDetailsOptions({
      path: {
        license_id: licenseId,
      },
    }),
    enabled: isAuthSetupComplete && isValidLicenseId,
  });

  const isRejectedLicense = licenseData ? isRejected(licenseData) : false;
  const queryClient = useQueryClient();

  const {
    configurationData,
    questionsData,
    isLoading: isConfigLoading,
    error: configError,
  } = useLicenseFormData({
    licenseTypeId: licenseData?.license_type.id,
    jurisdictionId: licenseData?.jurisdiction.id,
    isRenewal: true,
    includeFees: false,
  });

  const isOnPeriodsAndFeesStep = step === RenewLicenseStep.PeriodsAndFees;

  const feeAnswerOverrides = extractFeeAnswerOverrides(
    questionsData?.questions,
    licenseAnswers,
  );
  const omtQuestion = getRegistrationTaggedQuestion(
    questionsData?.questions,
    "OMT",
  );
  const omtPartyId = omtQuestion
    ? partyIdFromUnregisteredAnswer(
        licenseAnswers?.[omtQuestion.id] ??
          licenseData?.answers_to_additional_questions?.[omtQuestion.id],
      )
    : null;

  const {
    feesData,
    isLoading: areFeesLoading,
    error: feesError,
  } = useLicenseFormData({
    licenseTypeId: licenseData?.license_type.id,
    jurisdictionId: licenseData?.jurisdiction.id,
    licenseId,
    isRenewal: true,
    includeFees: isOnPeriodsAndFeesStep && !!licenseAnswers,
    omtPartyId,
    ...feeAnswerOverrides,
  });
  const isFormDataLoading =
    isConfigLoading || (isOnPeriodsAndFeesStep && areFeesLoading);
  const formDataError = configError || feesError;

  const navigate = useNavigate();

  // Use custom mutations hook
  const {
    handleRenewLicense,
    handleCreateCheckPayment,
    handleCreateInvoicePayment,
    handleMakePayment,
    processLicenseAnswers,
  } = useLicenseFormMutations({
    onSuccess: async (_licenseId) => {
      // Payment will be handled in handleSubmit with the correct payment method type
    },
  });

  // Mutation for updating license answers (for reapply flow)
  const updateAnswersMutation = useMutation({
    ...updateLicenseAdditionalQuestionsMutation(),
    onSuccess: async () => {
      const renewalFeeQueryParams = {
        ...(feeAnswerOverrides.propertyStructureAnswer
          ? {
              property_structure_answer:
                feeAnswerOverrides.propertyStructureAnswer,
            }
          : {}),
        ...(feeAnswerOverrides.commercialManagedPropertyAnswer !== undefined
          ? {
              commercial_managed_property_answer:
                feeAnswerOverrides.commercialManagedPropertyAnswer,
            }
          : {}),
        ...(feeAnswerOverrides.numberOfUnitsAnswer !== undefined
          ? { number_of_units_answer: feeAnswerOverrides.numberOfUnitsAnswer }
          : {}),
        ...(feeAnswerOverrides.vacancyAnswer !== undefined
          ? { vacancy_answer: feeAnswerOverrides.vacancyAnswer }
          : {}),
      };

      await queryClient.refetchQueries({
        queryKey: getFeesForRenewalQueryKey({
          path: { license_id: licenseId },
          query: renewalFeeQueryParams,
        }),
      });
    },
  });

  // Handle step navigation
  const handleNextStep = async (answers: LicenseAnswers) => {
    setLicenseAnswers(answers);

    // For reapply, save the answers to the backend first
    if (isRejectedLicense) {
      const processedAnswers = processLicenseAnswers(answers);
      await updateAnswersMutation.mutateAsync({
        path: { license_id: licenseId },
        body: {
          answers_to_additional_questions: processedAnswers,
        },
      });
    }

    setStep(RenewLicenseStep.PeriodsAndFees);
    nextStep();
  };

  const handlePreviousStep = async () => {
    setStep(RenewLicenseStep.LicenseTypeQuestions);
    previousStep();
  };

  // Handle form submission
  const handleSubmit = async (
    data: PeriodsAndFeesData,
    paymentMethodType?:
      | "card"
      | "us_bank_account"
      | "check"
      | "invoice"
      | "pay_later"
      | "no_payment",
  ) => {
    if (!licenseAnswers || !licenseData) {
      console.error("Missing required data for renewal");
      return;
    }

    const answers = processLicenseAnswers({
      ...licenseAnswers,
      ...(data.attestation_answers ?? {}),
    });

    try {
      await handleRenewLicense({
        license_id: licenseData.id,
        effective_date: licenseAnswers.effective_date,
        answers_to_additional_questions: answers,
        periods: data.periods,
        registrant_note: data.registrant_note,
        pay_later_bulk_cohort: paymentMethodType === "pay_later",
      });

      // Pay later updates the license to PAY_LATER; is_renewable becomes false on refetch.
      // Leave the renew route immediately (same as new-license.tsx) so we do not flash
      // "License not renewable" after a successful submission.
      if (paymentMethodType === "pay_later") {
        await invalidateRegistrantDashboardLists(queryClient);
        navigate({
          to: "/registrant/licenses/$licenseId/view",
          params: { licenseId: licenseData.id },
        });
        return;
      }

      if (paymentMethodType === "no_payment") {
        await invalidateRegistrantDashboardLists(queryClient);
        navigate({
          to: "/registrant/licenses/$licenseId/view",
          params: { licenseId: licenseData.id },
        });
        return;
      }

      if (paymentMethodType === "check") {
        const result = await handleCreateCheckPayment(
          licenseData.id,
          data.periods,
          data.inspection_fee,
        );
        await invalidateRegistrantDashboardLists(queryClient);
        setCheckPaymentSuccess({
          displayId: result.check_id,
          checkMailingAddress: result.check_mailing_address,
          variant: "check",
        });
        return;
      }

      if (paymentMethodType === "invoice") {
        const result = await handleCreateInvoicePayment(
          licenseData.id,
          data.periods,
          data.inspection_fee,
        );
        await invalidateRegistrantDashboardLists(queryClient);
        setCheckPaymentSuccess({
          displayId: result.check_id,
          checkMailingAddress: result.check_mailing_address,
          variant: "invoice",
        });
        return;
      }

      // Make payment using the pending records created by handleRenewLicense
      if (
        paymentMethodType === "card" ||
        paymentMethodType === "us_bank_account"
      ) {
        await handleMakePayment(licenseData.id, paymentMethodType);
      }
    } catch (e) {
      void invalidateRegistrantDashboardLists(queryClient);
      throw e;
    }
  };

  // Early returns for loading and error states
  if (!isValidLicenseId) {
    return (
      <ErrorComponent
        title="Invalid URL"
        message={`The license ID '${licenseId}' is not a valid license ID`}
      />
    );
  }

  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }

  if (isQueryLoading || isFormDataLoading) {
    return <LoadingScreen />;
  }

  if (queryError || formDataError) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(queryError || formDataError)}
        message={getQueryErrorMessage(queryError || formDataError)}
      />
    );
  }

  if (!licenseData || !configurationData || !questionsData) {
    return <LoadingScreen message="Loading form data..." />;
  }

  if (isOnPeriodsAndFeesStep && !feesData) {
    return <LoadingScreen message="Loading form data..." />;
  }

  if (!licenseData?.is_renewable && !isRejectedLicense) {
    return (
      <ErrorComponent
        title="License not renewable"
        message="License is not in a status that allows it to be renewed."
      />
    );
  }

  return (
    <div
      data-slot="registration-form-scroll"
      className="flex min-h-0 flex-1 flex-col overflow-y-auto container mx-auto max-w-3xl"
    >
      <CheckPaymentSuccessDialog
        isOpen={!!checkPaymentSuccess}
        onOpenChange={(open) => {
          if (!open) setCheckPaymentSuccess(null);
        }}
        displayId={checkPaymentSuccess?.displayId ?? ""}
        checkMailingAddress={checkPaymentSuccess?.checkMailingAddress}
        variant={checkPaymentSuccess?.variant ?? "check"}
        onOk={() => {
          if (checkPaymentSuccess) {
            navigate({
              to: "/registrant/licenses/$licenseId/view",
              params: { licenseId },
            });
          }
        }}
      />
      <PageHeader
        title={isRejectedLicense ? "Reapply" : "Renew Registration"}
        subtitle={
          isRejectedLicense
            ? "Update your information and resubmit your registration"
            : "Complete the form below to renew your registration"
        }
        className="mb-6"
        action={<ContextHelpButton articleId="renewals" />}
      />

      {step === RenewLicenseStep.LicenseTypeQuestions && (
        <LicenseTypeQuestionsForm
          onNext={handleNextStep}
          licenseType={licenseData.license_type}
          initialAnswers={
            licenseAnswers ?? {
              effective_date: licenseData?.effective_date,
              ...licenseData?.answers_to_additional_questions,
            }
          }
          stepNumber={stepNumber}
          isRenewal={true}
          licenseData={licenseData}
          questionsData={questionsData}
        />
      )}

      {step === RenewLicenseStep.PeriodsAndFees && feesData && (
        <PeriodsAndFeesForm
          onPrevious={handlePreviousStep}
          onSubmit={handleSubmit}
          stepNumber={stepNumber}
          feesData={feesData}
          configurationData={configurationData}
          isRenewal={true}
          isReapply={isRejectedLicense}
          effectiveDateLabel={licenseData.license_type.effective_date_label}
          paymentScreenQuestions={questionsData?.questions}
          paymentScreenAnswers={{
            ...licenseData.answers_to_additional_questions,
            ...(licenseAnswers ?? {}),
          }}
        />
      )}
    </div>
  );
}
